type TLogLevel = 'debug' | 'log' | 'warn' | 'error' | 'off';

interface ILoggerConfig {
  level: TLogLevel;
  prefix?: string;
  prefixColor?: string;
  disabledPrefixes?: string[];
}

export class Logger {
  private level: TLogLevel;
  private prefix?: string;
  private prefixColor?: string = '#ff9900';
  private disabledPrefixes: string[];
  private prefixColorMap: Record<string, string> = {};
  private defaultPrefixColor: string = 'inherit';

  constructor(config: ILoggerConfig) {
    this.level = config.level;
    this.prefix = config.prefix;
    this.prefixColor = config.prefixColor;
    this.disabledPrefixes = config.disabledPrefixes || [];
  }

  private shouldLog(level: TLogLevel, prefix?: string): boolean {
    const levels: TLogLevel[] = ['debug', 'log', 'warn', 'error', 'off'];
    const currentLevelIndex = levels.indexOf(this.level);
    const messageLevelIndex = levels.indexOf(level);

    if (prefix && this.disabledPrefixes.includes(prefix)) {
      return false;
    }

    return messageLevelIndex >= currentLevelIndex;
  }

  private formatMessage(message: unknown): unknown {
    if (typeof message !== 'string') {
      return message;
    }
    const prefix = this.extractPrefix(message);
    if (this.prefix) {
      return `%c[${prefix}] ${message}`;
    }
    return message;
  }

  private getColor(message: unknown): string {
    if (typeof message !== 'string') {
      return this.defaultPrefixColor;
    }
    const prefix = this.extractPrefix(message);
    return prefix && this.prefixColorMap[prefix] ? this.prefixColorMap[prefix] : this.defaultPrefixColor;
  }

  private extractPrefix(message: unknown): string | undefined {
    if (typeof message !== 'string') {
      return;
    }
    const match = message.match(/\[(.*?)\]/);
    if (match) {
      message = message.replace(match[0], '').trim();
      return match[1];
    }
    return this.prefix;
  }

  debug(...messages: unknown[]): void {
    const prefix = this.extractPrefix(messages[0]);
    if (this.shouldLog('debug', prefix)) {
      console.debug(
        `%c ${this.formatMessage(messages[0])}`,
        this.prefixColor ? `color: ${this.getColor(messages[0])};` : '',
        ...messages.slice(1)
      );
    }
  }

  info(...messages: unknown[]): void {
    const prefix = this.extractPrefix(messages[0]);
    if (this.shouldLog('log', prefix)) {
      console.info(
        `%c ${this.formatMessage(messages[0])}`,
        this.prefixColor ? `color: ${this.getColor(messages[0])};` : '',
        ...messages.slice(1)
      );
    }
  }
  log(...messages: unknown[]): void {
    const prefix = this.extractPrefix(messages[0]);
    if (this.shouldLog('log', prefix)) {
      console.log(
        `%c ${this.formatMessage(messages[0])}`,
        this.prefixColor ? `color: ${this.getColor(messages[0])};` : '',
        ...messages.slice(1)
      );
    }
  }

  warn(...messages: unknown[]): void {
    const prefix = this.extractPrefix(messages[0]);
    if (this.shouldLog('warn', prefix)) {
      console.warn(
        `%c ${this.formatMessage(messages[0])}`,
        this.prefixColor ? `color: ${this.getColor(messages[0])};` : '',
        ...messages.slice(1)
      );
    }
  }

  error(...messages: unknown[]): void {
    const prefix = this.extractPrefix(messages[0]);
    if (this.shouldLog('error', prefix)) {
      console.error(
        `%c ${this.formatMessage(messages[0])}`,
        this.prefixColor ? `color: ${this.getColor(messages[0])};` : '',
        ...messages.slice(1)
      );
    }
  }

  updateConfig(config: Partial<ILoggerConfig>): void {
    if (config.prefixColor) {
      this.prefixColor = config.prefixColor;
    }
    if (config.disabledPrefixes) {
      this.disabledPrefixes = config.disabledPrefixes;
    }
  }

  setPrefixColor(prefix: string, color: string): void {
    this.prefixColorMap[prefix] = color;
  }
}

const getLogLevelFromEnv = (): TLogLevel => {
  const envLevel = process.env.LOG_LEVEL as TLogLevel;
  const urlParams = new URLSearchParams(window.location.search);
  const urlLevel = urlParams.get('log') as TLogLevel;

  if (urlLevel) {
    return urlLevel;
  }

  if (envLevel) {
    return envLevel;
  }

  return import.meta.env.MODE === 'production' ? 'off' : 'debug';
};

const logger = new Logger({
  level: getLogLevelFromEnv(),
  prefixColor: 'blue',
  disabledPrefixes: ['NoLog'],
});

logger.setPrefixColor('VOIP', 'gold');
logger.setPrefixColor('SPEAKER DETECTOR', 'salmon');

export default logger;
